import React from 'react';
// ─── L'échelle d'intensité — une seule, pour tout le produit ─────────────────
// Trois rapports coloraient leurs matrices avec trois fonctions différentes :
// une rampe verte pour l'APV, une bleue pour le VN et les fournisseurs, deux
// exposants (0,7 et 0,85), et deux échelles divergentes dont l'une bornée par
// des centiles et l'autre par le min/max. La même case pouvait donc être plus
// foncée dans un onglet que dans l'autre à valeur identique.
//
// Deux échelles, pas une de plus (CLAUDE.md §5) :
//   • SÉQUENTIELLE — une grandeur qui n'a pas de point neutre (un CA, un
//     volume, des heures). Du blanc au vert du produit.
//   • DIVERGENTE — une grandeur qui se lit par rapport à une référence (un
//     taux face à sa moyenne, un écart N-1). Vert au-dessus, rouge en dessous.
//
// ⚠ La palette des sept couleurs (`COULEURS`) dit une IDENTITÉ — quelle
// société, quelle série. Elle n'a jamais le droit de dire une intensité, et
// réciproquement : sans cette séparation, un bleu pouvait signifier « SOGUAVA »
// dans un bloc et « valeur élevée » dans le bloc d'à côté.



// L'exposant 0,85 plutôt que 1 : les distributions sont très asymétriques (une
// concession fait souvent la moitié du total), et une rampe linéaire laissait
// tout le reste du tableau blanc.
export function sequentielle(v, max) {
  if (v == null) return { bg: 'transparent', mut: true };
  const t = max ? Math.max(0, v) / max : 0;
  // color-mix sur des variables du thème : le dégradé suit le mode nuit sans
  // recalcul (même interpolation linéaire que l'ancien lerp en RVB).
  return { bg: `color-mix(in srgb, var(--hm-haut) ${(Math.pow(t, 0.85) * 100).toFixed(1)}%, var(--hm-bas))`, fg: t > 0.55 ? '#fff' : null };
}

// `sens` dit de quel côté se trouve la bonne nouvelle : +1 quand monter est
// favorable (un taux de marge), -1 quand c'est descendre (une charge
// fournisseur). Sans lui, le même rouge aurait voulu dire « sous la moyenne »
// dans un onglet et « au-dessus de N-1 » dans l'autre.
export function divergente(v, { mid = 0, min = 0, max = 1, sens = 1 }) {
  if (v == null) return { bg: 'transparent', mut: true };
  const haut = v >= mid;
  const t = haut ? Math.min(1, (v - mid) / (max - mid || 1))
                 : Math.min(1, (mid - v) / (mid - min || 1));
  const favorable = haut === (sens > 0);
  const cible = favorable ? 'var(--hm-fav)' : 'var(--hm-def)';
  const encre = favorable ? 'var(--tbl-intensite)' : 'var(--tbl-defavorable)';
  return { bg: `color-mix(in srgb, ${cible} ${(t * 100).toFixed(1)}%, var(--hm-neutre))`, fg: t > 0.6 ? encre : null };
}

// Teinte d'arrière-plan légère, pour une colonne isolée dans un tableau qui
// n'est pas une matrice (le CA du jour). Bornée à 45 % : au-delà, le texte noir
// ne passe plus.
export function teinte(v, mn, mx) {
  if (v == null) return 'transparent';
  const t = mx > mn ? (v - mn) / (mx - mn) : 0;
  return `color-mix(in srgb, var(--tbl-intensite, #0F766E) ${(5 + t * 40).toFixed(0)}%, var(--bg-surface, #fff))`;
}

// ─── La légende : dessinée à partir des MÊMES fonctions que les cellules ─────
// Trois légendes montraient encore une rampe bleue alors que les cellules
// étaient passées au vert : une légende recopiée à la main se désynchronise.
export function Echelle({ divergent = false, sens = 1 }) {
  const cases = divergent
    ? [-1, -0.55, 0, 0.55, 1].map((x) => divergente(x, { mid: 0, min: -1, max: 1, sens }).bg)
    : [0.06, 0.4, 1].map((x) => sequentielle(x, 1).bg);
  return <span className="scale">{cases.map((bg, i) => <i key={i} style={{ background: bg }} />)}</span>;
}
